import { createRef, RefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeDealData, requestCampaignNameFocus } from 'store/dealManagement/reducer';
import useIsReadOnly from 'components/pages/Planner/useIsReadOnly';
import { Store } from 'components/common/types/Store.types';
import { InputBlurHandler } from 'components/common/types/Input.types';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { getTimezone } from 'store/publisher/selectors';

const CAMPAIGN_NAME_DATE_TEMPLATE = `yyyyMMddHHmm`;

export type UseCampaignNameOptions = {
  autoFocus?: boolean;
  autoFillOnBlur?: boolean;
  campaignNameSuffix?: string;
};

type UseCampaignNameAutoFocus = (options?: UseCampaignNameOptions) => {
  inputRef: RefObject<HTMLInputElement>;
  isDisabled: boolean;
  onBlur: InputBlurHandler;
};

export const useCampaignNameAutoFocus: UseCampaignNameAutoFocus = (
  { autoFocus, autoFillOnBlur, campaignNameSuffix } = {
    autoFocus: false,
    autoFillOnBlur: false,
    campaignNameSuffix: '',
  },
) => {
  const inputRef = createRef<HTMLInputElement>();
  const dispatch = useDispatch();

  const { isCampaignReadOnly } = useIsReadOnly();
  const dealName = useSelector((state: Store) => state.dealManagement.commonDeal.dealName);
  const isFocusRequested = useSelector((state: Store) => state.dealManagement.isCampaignNameFocusRequested);

  const timezone = useSelector(getTimezone);
  const currentDateTimeInUTC = utcToZonedTime(new Date(), timezone);

  const onCampaignNameBlur = (): void => {
    if (!autoFillOnBlur || dealName) return;

    dispatch(
      changeDealData({
        dealName: `${format(currentDateTimeInUTC, CAMPAIGN_NAME_DATE_TEMPLATE)}_${campaignNameSuffix}`,
      }),
    );
  };

  useEffect(() => {
    if (!autoFocus) return;

    const clearFocusRequest = (): void => {
      if (isFocusRequested) dispatch(requestCampaignNameFocus(false));
    };

    if (!inputRef.current || isCampaignReadOnly) {
      clearFocusRequest();
      return;
    }

    if (dealName && !isFocusRequested) return;

    inputRef.current.focus();
    clearFocusRequest();
  }, [dealName, isCampaignReadOnly, isFocusRequested]);

  return {
    inputRef,
    isDisabled: isCampaignReadOnly,
    onBlur: onCampaignNameBlur,
  };
};
